const SUPPORTED_ENTITIES = ['company', 'site', 'unit', 'personnel'] as const;

export type SupportedEntity = (typeof SUPPORTED_ENTITIES)[number];

export interface FieldSourceInfo {
  entity: SupportedEntity;
  path: string;
  paths: string[];
  raw: string;
}

/**
 * Parse a template source string like "{{company.name}}" and extract entity metadata.
 * Returns null when the source does not reference a supported entity.
 */
export function parseFieldSource(source?: string | null): FieldSourceInfo | null {
  if (!source) return null;
  const trimmed = source.trim();

  const matches = Array.from(trimmed.matchAll(/\{\{\s*((?:[^}]|\}(?!\}))+?)\s*\}\}/g));

  if (matches.length === 0) {
    return null;
  }

  const paths: string[] = [];
  let resolvedEntity: SupportedEntity | null = null;

  for (const match of matches) {
    const inner = match[1]?.trim();
    if (!inner) return null;

    // Discard pipe expressions (e.g., {{ unit.name | default('—') }})
    const [expression] = inner.split('|');
    if (!expression) return null;

    const segments = expression
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0);

    if (segments.length === 0) {
      return null;
    }

    const [entity, ...rest] = segments;
    if (!SUPPORTED_ENTITIES.includes(entity as SupportedEntity)) {
      return null;
    }

    const normalizedEntity = entity as SupportedEntity;
    if (resolvedEntity && resolvedEntity !== normalizedEntity) {
      return null;
    }
    resolvedEntity = normalizedEntity;

    const path = rest.join('.');
    if (!paths.includes(path)) {
      paths.push(path);
    }
  }

  if (!resolvedEntity) {
    return null;
  }

  return {
    entity: resolvedEntity,
    path: paths[0] ?? '',
    paths,
    raw: trimmed,
  };
}
